/*
  Global storefront bootstrap.
  Keep this file small; future components should register with HouseOfThiraa.events.
*/

(function () {
  'use strict';

  const theme = window.HouseOfThiraa;

  if (!theme) {
    return;
  }

  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  if (!theme.responsive.matches('motionSafe')) {
    document.body.classList.add('motion-disabled');
  }

  theme.responsive.onChange('motionReduce', (matches) => {
    document.body.classList.toggle('motion-disabled', matches);
  });

  theme.editor.bindLifecycle();

  const initPreloader = () => {
    const preloader = document.querySelector('[data-preloader]');

    if (!preloader || preloader.dataset.preloaderInitialized === 'true') {
      return;
    }

    preloader.dataset.preloaderInitialized = 'true';
    const storageKey = 'houseOfThiraaPreloaderShown:v2';
    const isDesignMode = preloader.dataset.designMode === 'true' || window.Shopify?.designMode === true;
    const showOnce = preloader.dataset.showOnce === 'true' && !isDesignMode;
    const isReducedMotion = theme.responsive.matches('motionReduce');
    const minDuration = isReducedMotion ? 0 : Math.max(Number(preloader.dataset.minDuration) || 1800, 0);
    const maxDuration = isReducedMotion ? 500 : Math.max(Number(preloader.dataset.maxDuration) || 3000, minDuration);
    const exitDuration = isReducedMotion ? 1 : Math.max(Number(preloader.dataset.exitDuration) || 400, 0);
    let isHidden = false;
    let isPageReady = document.readyState !== 'loading';
    let isMinimumElapsed = minDuration === 0;
    let hasDisplayed = false;
    const timers = new Set();

    const setTimer = (callback, delay) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        callback();
      }, delay);
      timers.add(timer);
      return timer;
    };

    const clearTimers = () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };

    const markShown = () => {
      if (!showOnce || !hasDisplayed) {
        return;
      }

      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch (error) {
        // Session storage can be unavailable in private contexts; the preloader still exits.
      }
    };

    const hasShown = () => {
      if (!showOnce) {
        return false;
      }

      try {
        return window.sessionStorage.getItem(storageKey) === 'true';
      } catch (error) {
        return false;
      }
    };

    const hidePreloader = () => {
      if (isHidden) {
        return;
      }

      isHidden = true;
      preloader.classList.add('is-leaving');
      setTimer(() => {
        clearTimers();
        markShown();
        preloader.remove();
      }, exitDuration + 50);
    };

    const requestHide = () => {
      if (hasDisplayed && isPageReady && isMinimumElapsed) {
        hidePreloader();
      }
    };

    if (hasShown()) {
      preloader.remove();
      return;
    }

    const beginLifecycle = () => {
      if (isHidden) {
        return;
      }

      hasDisplayed = true;
      preloader.classList.add('is-visible');
      requestHide();
    };

    window.requestAnimationFrame(() => {
      beginLifecycle();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        isPageReady = true;
        requestHide();
      }, { once: true });
    } else {
      window.requestAnimationFrame(requestHide);
    }

    setTimer(() => {
      isMinimumElapsed = true;
      requestHide();
    }, minDuration);

    setTimer(hidePreloader, maxDuration);
  };

  initPreloader();

  const revealSelector = [
    '.animate-fade-in',
    '[data-motion-reveal]'
  ].join(',');

  const homepageRevealSelector = [
    '#MainContent > .shopify-section:not(:first-child) h2',
    '#MainContent > .shopify-section:not(:first-child) h3',
    '#MainContent > .shopify-section:not(:first-child) .media',
    '#MainContent > .shopify-section:not(:first-child) .card',
    '#MainContent > .shopify-section:not(:first-child) .product-card',
    '#MainContent > .shopify-section:not(:first-child) .collection-card',
    '#MainContent > .shopify-section:not(:first-child) .editorial-card',
    '#MainContent > .shopify-section:not(:first-child) [data-product-card]',
    '#MainContent > .shopify-section:not(:first-child) [data-collection-card]',
    '#MainContent > .shopify-section:not(:first-child) [data-editorial-image]',
    '#MainContent > .shopify-section:not(:first-child) .button'
  ].join(',');

  const getRevealTargets = (scope = document) => {
    const explicitTargets = Array.from(scope.querySelectorAll(revealSelector));

    if (!document.body.classList.contains('template-index')) {
      return explicitTargets;
    }

    const homepageTargets = Array.from(scope.querySelectorAll(homepageRevealSelector)).filter((element) => {
      return !element.closest('[data-motion="off"]');
    });

    return Array.from(new Set([...explicitTargets, ...homepageTargets]));
  };

  const prepareRevealTargets = (targets) => {
    const initialViewportBottom = window.scrollY + window.innerHeight;

    return targets.filter((element, index) => {
      if (element.classList.contains('is-reveal-visible')) {
        return false;
      }

      const bounds = element.getBoundingClientRect();
      const elementTop = bounds.top + window.scrollY;

      if (elementTop < initialViewportBottom) {
        element.classList.add('is-visible', 'is-reveal-visible');
        return false;
      }

      element.classList.add('is-reveal-pending');
      element.style.setProperty('--motion-reveal-delay', `${Math.min(index * 50, 250)}ms`);
      element.style.transitionDelay = 'var(--motion-reveal-delay)';
      return true;
    });
  };

  const initRevealAnimations = (scope = document) => {
    if (document.body.classList.contains('motion-disabled')) {
      return () => {};
    }

    const targets = prepareRevealTargets(getRevealTargets(scope));

    return theme.lazy.observe(targets, (element) => {
      element.classList.remove('is-reveal-pending');
      element.classList.add('is-visible', 'is-reveal-visible');
    });
  };

  const revealDisconnectors = new Set();

  const refreshRevealAnimations = (scope = document) => {
    const disconnect = initRevealAnimations(scope);

    if (typeof disconnect === 'function') {
      revealDisconnectors.add(disconnect);
    }

    return disconnect;
  };

  const disconnectRevealAnimations = () => {
    revealDisconnectors.forEach((disconnect) => disconnect());
    revealDisconnectors.clear();
  };

  refreshRevealAnimations();

  theme.events.on('section:load', (event) => {
    refreshRevealAnimations(event.detail.target);
  });

  theme.events.on('section:unload', () => {
    disconnectRevealAnimations();
    refreshRevealAnimations();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      theme.events.emit('keyboard:escape', { originalEvent: event });
    }
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-dispatch-event]');

    if (!trigger) {
      return;
    }

    theme.events.emit(trigger.dataset.dispatchEvent, {
      trigger,
      originalEvent: event
    });
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      theme.events.emit('page:restore');
    }
  });

  theme.events.emit('theme:ready');
})();
